<!--
  Таблица связанных объектов для поля паспорта с view_type: table.
  Колонки: ID, Наименование, Действия (Просмотр / Карта / Отвязать).
-->
<script setup lang="ts">
import './assets/object-passport-linked-table.scss'
import { computed, ref, toRef, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useTranslate } from '@/shared/lib'
import { VIcons } from '@/shared/ui'
import { coordsPresent, navigateToMapView, navigateToPlaceOnMap } from '@/features/Inventory/PlaceOnMap'
import ObjectPassportLinkTableModal from './ObjectPassportLinkTableModal.vue'
import {
  isObjectPassportDisplayField,
  resolveFieldInventoryTypeId,
} from '../model/objectPassportFieldAdapter'
import { resolveLinkedChildPassportLocation } from '../model/resolveLinkedChildPassportRoute'
import type { ObjectPassportFieldDefinition, ObjectPassportFieldValue } from '../model/types'
import { useObjectPassportLinkedTable } from '../model/useObjectPassportLinkedTable'
import { buildLinkPayload } from '../model/objectPassportLinkedLinkPayload'
import { usePendingFieldLinksOptional } from '../model/usePendingFieldLinks'

const props = withDefaults(
  defineProps<{
    objectListId: number
    definition: ObjectPassportFieldDefinition
    value?: ObjectPassportFieldValue | null
  /** Route map group id (`object_id`) — для навигации на карту. */
  inventoryGroupObjectId?: string
  fieldsLocked?: boolean
  label?: string
  /** Срез версии для чтения связей («состояние на дату»); пусто = текущие. */
  statusDate?: string | null
  }>(),
  {
    value: null,
    inventoryGroupObjectId: '',
    fieldsLocked: false,
    label: '',
    statusDate: null,
  },
)

const { trs } = useTranslate()
const route = useRoute()
const router = useRouter()
const pendingFieldLinks = usePendingFieldLinksOptional()

const fieldId = computed(() => Number(props.definition.id) || 0)

const page = ref(1)
const perPage = ref(10)
const linkModalOpen = ref(false)

const fieldRef = toRef(props, 'definition')
const objectListIdRef = toRef(props, 'objectListId')
const statusDateRef = toRef(props, 'statusDate')
const pagination = computed(() => ({ page: page.value, perPage: perPage.value }))

watch(
  () => props.definition.id,
  () => {
    page.value = 1
  },
)

const { rows, meta, isLoading, isError, refetch } = useObjectPassportLinkedTable({
  objectListId: objectListIdRef,
  field: fieldRef,
  pagination,
  statusDate: statusDateRef,
})

const tableTitle = computed(() => props.label?.trim() || props.definition.name || '—')
const linksReadonly = computed(() => isObjectPassportDisplayField(props.definition))
const canLink = computed(() => props.inventoryGroupObjectId.length > 0 && resolveFieldInventoryTypeId(props.definition) > 0)
const total = computed(() => meta.value.total)
const totalPages = computed(() => Math.max(1, Math.ceil(total.value / perPage.value) || 1))

watch(totalPages, (len) => {
  if (page.value > len) page.value = len
})

const paginationStart = computed(() => {
  if (total.value === 0) return 0
  return (page.value - 1) * perPage.value + 1
})

const paginationEnd = computed(() => Math.min(page.value * perPage.value, total.value))

function childPassportTo(childId: number) {
  return resolveLinkedChildPassportLocation(route, router, childId)
}

function mapTooltip(hasGeom: boolean): string {
  return hasGeom ? trs('itf.passport_runtime_list_action_map') : trs('itf.place_on_map')
}

function onMapRowAction(childId: number, coordinates: unknown) {
  const hasGeom = coordsPresent(coordinates)
  if (hasGeom) {
    navigateToMapView(router, route, childId)
  } else {
    navigateToPlaceOnMap(router, route, childId)
  }
}

/** Желаемый список id с учётом ожидающей патча (иначе серверный value_list). */
function desiredListOfPatch(): number[] {
  const patch = pendingFieldLinks.patchFor(fieldId.value)
  if (!patch) return serverValueList()
  const list = patch.payload.value_list
  if (list && list.length) return list
  const single = Number(patch.payload.value ?? 0)
  return single > 0 ? [single] : []
}

function serverValueList(): number[] {
  const list = props.value?.value_list
  if (list?.length) return list.filter((id) => Number.isFinite(id) && id > 0)
  const single = Number(props.value?.value ?? 0)
  return single > 0 ? [single] : []
}

const pendingPatch = computed(() => pendingFieldLinks.patchFor(fieldId.value))
const pendingRemovedSet = computed(() => new Set(pendingPatch.value?.removedIds ?? []))

/** Строки с оверлеем: серверные минус удалённые + новые (с пометкой). */
const mergedRows = computed(() => {
  const removed = pendingRemovedSet.value
  const base = rows.value.filter((r) => !removed.has(r.objectId)).map((r) => ({
    objectId: r.objectId,
    name: r.name,
    coordinates: r.coordinates,
    pendingKind: undefined as 'link' | 'unlink' | undefined,
  }))
  const added = (pendingPatch.value?.addedRows ?? []).map((r) => ({
    objectId: r.objectId,
    name: r.name,
    coordinates: undefined,
    pendingKind: 'link' as const,
  }))
  return [...added, ...base]
})

const mergedTotal = computed(() => mergedRows.value.length)

function pendingStateFor(objectId: number): 'link' | 'unlink' | null {
  if (pendingPatch.value?.addedRows.some((r) => r.objectId === objectId)) return 'link'
  if (pendingRemovedSet.value.has(objectId)) return 'unlink'
  return null
}

function rowPendingClass(objectId: number): string {
  const st = pendingStateFor(objectId)
  if (st === 'link') return 'object-passport-linked-table__row--pending-link'
  if (st === 'unlink') return 'object-passport-linked-table__row--pending-unlink'
  return ''
}

function onUndoPending(): void {
  pendingFieldLinks.undo(fieldId.value)
}

function onUnlinkRow(childId: number): void {
  if (props.fieldsLocked) return
  const existing = pendingFieldLinks.patchFor(fieldId.value)
  const addedRows = existing?.addedRows ?? []
  if (addedRows.some((r) => r.objectId === childId)) {
    const nextList = desiredListOfPatch().filter((id) => id !== childId)
    pendingFieldLinks.stage({
      fieldId: fieldId.value,
      payload: buildLinkPayload(props.definition, nextList),
      addedRows: addedRows.filter((r) => r.objectId !== childId),
      removedIds: existing?.removedIds ?? [],
    })
    return
  }
  const nextList = desiredListOfPatch().filter((id) => id !== childId)
  const nextRemoved = Array.from(new Set([...(existing?.removedIds ?? []), childId]))
  pendingFieldLinks.stage({
    fieldId: fieldId.value,
    payload: buildLinkPayload(props.definition, nextList),
    addedRows,
    removedIds: nextRemoved,
  })
}
</script>

<template>
  <div class="object-passport-linked-table">
    <div class="object-passport-linked-table__header">
      <span class="object-passport-linked-table__header-title">{{ tableTitle }}</span>
      <div v-if="!fieldsLocked && !linksReadonly" class="object-passport-linked-table__header-actions">
        <VTooltip location="bottom">
          <template #activator="{ props: tip }">
            <span v-bind="tip" class="object-passport-linked-table__action-wrap">
              <VBtn
                icon
                variant="text"
                density="compact"
                :disabled="!canLink"
                :aria-label="trs('itf.passport_runtime_list_link')"
                @click="linkModalOpen = true"
              >
                <VIcons type="link" />
              </VBtn>
            </span>
          </template>
          <span>{{ trs('itf.passport_runtime_list_link') }}</span>
        </VTooltip>
      </div>
    </div>

    <div v-if="isLoading && !rows.length" class="object-passport-linked-table__state">
      <VProgressCircular indeterminate size="22" width="2" />
    </div>

    <VAlert
      v-else-if="isError"
      type="error"
      variant="tonal"
      density="compact"
      class="object-passport-linked-table__alert"
    >
      {{ trs('request.unknown_error') }}
      <template #append>
        <VBtn variant="text" size="small" @click="refetch()">Повторить</VBtn>
      </template>
    </VAlert>

    <template v-else>
      <div v-if="!mergedRows.length" class="object-passport-linked-table__empty">
        {{ trs('itf.passport_runtime_tab_no_fields') }}
      </div>

      <div v-else class="object-passport-linked-table__grid">
        <div class="object-passport-linked-table__head">
          <div class="object-passport-linked-table__th object-passport-linked-table__th--id">
            {{ trs('itf.id') }}
          </div>
          <div class="object-passport-linked-table__th object-passport-linked-table__th--name">
            {{ trs('itf.name_label') }}
          </div>
          <div class="object-passport-linked-table__th object-passport-linked-table__th--actions">
            Действия
          </div>
        </div>

        <div
          v-for="row in mergedRows"
          :key="row.objectId"
          :class="['object-passport-linked-table__row', rowPendingClass(row.objectId)]"
        >
          <div class="object-passport-linked-table__td object-passport-linked-table__td--id">
            {{ row.objectId }}
          </div>
          <div class="object-passport-linked-table__td object-passport-linked-table__td--name">
            <RouterLink class="object-passport-linked-table__name-link" :to="childPassportTo(row.objectId)">
              {{ row.name }}
            </RouterLink>
            <span
              v-if="pendingStateFor(row.objectId) === 'link'"
              class="object-passport-linked-table__badge object-passport-linked-table__badge--new"
            >{{ trs('itf.passport_runtime_list_pending_new') }}</span>
            <span
              v-else-if="pendingStateFor(row.objectId) === 'unlink'"
              class="object-passport-linked-table__badge object-passport-linked-table__badge--unlink"
            >{{ trs('itf.passport_runtime_list_pending_unlink') }}</span>
          </div>
          <div class="object-passport-linked-table__td object-passport-linked-table__td--actions">
            <VTooltip location="top">
              <template #activator="{ props: tip }">
                <span v-bind="tip" class="object-passport-linked-table__action-wrap">
                  <RouterLink
                    v-if="!fieldsLocked"
                    :to="childPassportTo(row.objectId)"
                    custom
                    v-slot="{ href, navigate }"
                  >
                    <VBtn
                      icon
                      variant="text"
                      density="compact"
                      :href="href"
                      :aria-label="trs('itf.view')"
                      @click="navigate"
                    >
                      <VIcons type="visibility" />
                    </VBtn>
                  </RouterLink>
                  <VBtn
                    v-else
                    icon
                    variant="text"
                    density="compact"
                    disabled
                    :aria-label="trs('itf.view')"
                  >
                    <VIcons type="visibility" />
                  </VBtn>
                </span>
              </template>
              <span>{{ trs('itf.view') }}</span>
            </VTooltip>

            <VTooltip location="top">
              <template #activator="{ props: tip }">
                <span v-bind="tip" class="object-passport-linked-table__action-wrap">
                  <VBtn
                    icon
                    variant="text"
                    density="compact"
                    :aria-label="mapTooltip(coordsPresent(row.coordinates))"
                    @click="onMapRowAction(row.objectId, row.coordinates)"
                  >
                    <VIcons :type="coordsPresent(row.coordinates) ? 'location_on' : 'add_location_alt'" />
                  </VBtn>
                </span>
              </template>
              <span>{{ mapTooltip(coordsPresent(row.coordinates)) }}</span>
            </VTooltip>

            <VTooltip v-if="!linksReadonly" location="top">
              <template #activator="{ props: tip }">
                <span v-bind="tip" class="object-passport-linked-table__action-wrap">
                  <VBtn
                    v-if="pendingStateFor(row.objectId)"
                    icon
                    variant="text"
                    density="compact"
                    :aria-label="trs('itf.passport_runtime_list_pending_undo')"
                    @click="onUndoPending"
                  >
                    <VIcons type="undo" />
                  </VBtn>
                  <VBtn
                    v-else
                    icon
                    variant="text"
                    density="compact"
                    :disabled="fieldsLocked"
                    :aria-label="trs('itf.passport_runtime_list_unlink')"
                    @click="onUnlinkRow(row.objectId)"
                  >
                    <VIcons type="link_off" />
                  </VBtn>
                </span>
              </template>
              <span>{{
                pendingStateFor(row.objectId)
                  ? trs('itf.passport_runtime_list_pending_undo')
                  : trs('itf.passport_runtime_list_unlink')
              }}</span>
            </VTooltip>
          </div>
        </div>
      </div>

      <div v-if="total > 0" class="object-passport-linked-table__pagination">
        <div class="object-passport-linked-table__pagination-per-page">
          <span>Записей на странице:</span>
          <VSelect
            v-model="perPage"
            :items="[10, 20, 50, 100]"
            density="compact"
            variant="outlined"
            hide-details
            @update:model-value="page = 1"
          />
        </div>
        <div class="object-passport-linked-table__pagination-info">
          {{ paginationStart }}–{{ paginationEnd }} из {{ total }}
        </div>
        <div class="object-passport-linked-table__pagination-nav">
          <VPagination
            v-model="page"
            :length="totalPages"
            density="comfortable"
            rounded
            show-first-last-page
            :total-visible="0"
            variant="plain"
            first-icon="keyboard_double_arrow_left"
            last-icon="keyboard_double_arrow_right"
            prev-icon="chevron_left"
            next-icon="chevron_right"
          />
        </div>
      </div>
    </template>

    <ObjectPassportLinkTableModal
      v-model="linkModalOpen"
      :object-list-id="objectListId"
      :field="definition"
      :value="value"
      :inventory-group-object-id="inventoryGroupObjectId"
    />
  </div>
</template>
